import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ContactSalesModal from '../components/modals/ContactSalesModal';
import {
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ShareIcon,
  UserGroupIcon,
  CircleStackIcon,
  ChartBarIcon,
  ClockIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  CreditCardIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Plan feature comparison data
const PLAN_FEATURES = {
  starter: {
    color: 'bg-surface-500',
    gradient: 'from-surface-400 to-surface-600',
    features: [
      { text: '1 User Seat', icon: UserGroupIcon, included: true },
      { text: '500 MB Storage', icon: CircleStackIcon, included: true },
      { text: '1 Dashboard', icon: ChartBarIcon, included: true },
      { text: 'Weekly Sync', icon: ClockIcon, included: true },
      { text: '100 One-time Credits', icon: SparklesIcon, included: true },
      { text: 'Community Support', icon: CheckIcon, included: true },
      { text: 'Basic Data Sources', icon: CheckIcon, included: true },
      { text: 'AI Features', icon: CheckIcon, included: true },
      { text: 'Advanced Analytics', icon: XMarkIcon, included: false },
      { text: 'Priority Support', icon: XMarkIcon, included: false },
      { text: 'Share 1 Dashboard', icon: ShareIcon, included: true }
    ]
  },
  basic: {
    color: 'bg-blue-500',
    gradient: 'from-blue-400 to-blue-600',
    features: [
      { text: '1 User Seat', icon: UserGroupIcon, included: true },
      { text: '10 GB Storage', icon: CircleStackIcon, included: true },
      { text: '3 Dashboards', icon: ChartBarIcon, included: true },
      { text: 'Daily Sync', icon: ClockIcon, included: true },
      { text: '400 Monthly Credits', icon: SparklesIcon, included: true },
      { text: 'Email Support', icon: CheckIcon, included: true },
      { text: 'All Data Sources', icon: CheckIcon, included: true },
      { text: 'AI Features', icon: CheckIcon, included: true },
      { text: 'Advanced Analytics', icon: XMarkIcon, included: false },
      { text: 'Priority Support', icon: XMarkIcon, included: false },
      { text: 'Share 1 Dashboard', icon: ShareIcon, included: true }
    ]
  },
  pro: {
    color: 'bg-indigo-500',
    gradient: 'from-indigo-400 to-purple-600',
    recommended: true,
    features: [
      { text: '5 User Seats', icon: UserGroupIcon, included: true },
      { text: '100 GB Storage', icon: CircleStackIcon, included: true },
      { text: 'Unlimited Dashboards', icon: ChartBarIcon, included: true },
      { text: 'Hourly Sync', icon: ClockIcon, included: true },
      { text: '800 Monthly Credits', icon: SparklesIcon, included: true },
      { text: 'Priority Support', icon: CheckIcon, included: true },
      { text: 'All Data Sources', icon: CheckIcon, included: true },
      { text: 'AI Features', icon: CheckIcon, included: true },
      { text: 'Advanced Analytics', icon: CheckIcon, included: true },
      { text: 'API Access', icon: CheckIcon, included: true },
      { text: 'Share up to 5 Dashboards', icon: ShareIcon, included: true }
    ]
  },
  business: {
    color: 'bg-purple-500',
    gradient: 'from-purple-400 to-pink-600',
    features: [
      { text: '10 User Seats', icon: UserGroupIcon, included: true },
      { text: '500 GB Storage', icon: CircleStackIcon, included: true },
      { text: 'Unlimited Dashboards', icon: ChartBarIcon, included: true },
      { text: 'Hourly Sync', icon: ClockIcon, included: true },
      { text: '3,000 Monthly Credits', icon: SparklesIcon, included: true },
      { text: 'Dedicated Support', icon: CheckIcon, included: true },
      { text: 'All Data Sources', icon: CheckIcon, included: true },
      { text: 'AI Features', icon: CheckIcon, included: true },
      { text: 'Advanced Analytics', icon: CheckIcon, included: true },
      { text: 'Full API Access', icon: CheckIcon, included: true },
      { text: 'Custom Integrations', icon: CheckIcon, included: true },
      { text: 'SLA Guarantee', icon: CheckIcon, included: true },
      { text: 'Share up to 50 Dashboards', icon: ShareIcon, included: true }
    ]
  },
  enterprise: {
    color: 'bg-amber-500',
    gradient: 'from-amber-400 to-orange-600',
    features: [
      { text: 'Unlimited User Seats', icon: UserGroupIcon, included: true },
      { text: 'Custom Storage', icon: CircleStackIcon, included: true },
      { text: 'Unlimited Dashboards', icon: ChartBarIcon, included: true },
      { text: 'Real-time Sync', icon: ClockIcon, included: true },
      { text: 'Custom Credits', icon: SparklesIcon, included: true },
      { text: '24/7 Premium Support', icon: CheckIcon, included: true },
      { text: 'All Data Sources', icon: CheckIcon, included: true },
      { text: 'AI Features', icon: CheckIcon, included: true },
      { text: 'Advanced Analytics', icon: CheckIcon, included: true },
      { text: 'Full API Access', icon: CheckIcon, included: true },
      { text: 'Custom Integrations', icon: CheckIcon, included: true },
      { text: 'SLA Guarantee', icon: CheckIcon, included: true },
      { text: 'Dedicated Account Manager', icon: CheckIcon, included: true },
      { text: 'Custom Onboarding', icon: CheckIcon, included: true },
      { text: 'Unlimited Dashboard Sharing', icon: ShareIcon, included: true }
    ]
  }
};

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, getCurrentCompany, getCurrentCompanyId } = useAuthStore();
  const company = getCurrentCompany();
  const companyId = getCurrentCompanyId();
  
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  
  // Fetch subscription plans
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: () => apiHelpers.getSubscriptionPlans()
  });
  
  // Fetch available currencies
  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => apiHelpers.getCurrencies()
  });
  
  // Fetch credit packs
  const { data: creditPacksData } = useQuery({
    queryKey: ['creditPacks'],
    queryFn: () => apiHelpers.getCreditPacks()
  });
  
  // Fetch additional seats pricing
  const { data: seatsPricingData } = useQuery({
    queryKey: ['additionalSeatsPricing'],
    queryFn: () => apiHelpers.getAdditionalSeatsPricing()
  });
  
  const plans = plansData?.data?.plans || [];
  const currencies = currenciesData?.data?.currencies || [];
  const defaultCurrency = currenciesData?.data?.defaultCurrency || currencies.find(c => c.isDefault) || currencies[0];
  const creditPacks = creditPacksData?.data?.packs || [];
  const seatsPricing = seatsPricingData?.data?.pricing || [];
  
  // Initialize selected currency
  useEffect(() => {
    if (!selectedCurrency && defaultCurrency?.code) {
      setSelectedCurrency(defaultCurrency.code);
    }
  }, [defaultCurrency, selectedCurrency]);
  
  // Format currency amount
  const formatCurrencyAmount = (amount, currencyCode = 'USD') => {
    if (currencyCode === 'IDR') {
      return `Rp ${amount.toLocaleString('id-ID')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Get price for selected currency
  const getPriceForCurrency = (plan) => {
    return plan.pricing?.find(p => p.currencyCode === selectedCurrency);
  };
  
  // Format plan price
  const formatPrice = (plan) => {
    if (plan.isContactSales) return 'Custom';
    const price = getPriceForCurrency(plan);
    if (!price) return 'Contact Sales';
    if (price.amount === 0) return 'Free';
    return formatCurrencyAmount(price.amount, selectedCurrency);
  };
  
  // Handle plan selection
  const handleSelectPlan = (plan) => {
    if (plan.isContactSales) {
      // Show contact sales modal
      setShowContactSalesModal(true);
      return;
    }
    
    if (!isAuthenticated) {
      // Navigate to registration with plan pre-selected
      navigate(`/register?plan=${plan.slug}`);
    } else {
      // Navigate to billing page for upgrades
      navigate('/admin/billing');
    }
  };
  
  // Get current plan (if user is logged in)
  const currentPlan = company?.billingPlan || 'starter';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-50 to-white dark:from-surface-950 dark:to-surface-900">
      {/* Header */}
      <div className="bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white">
                  Neoshift BI
                </h1>
                <p className="text-xs text-surface-500">Business Intelligence Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="btn-ghost text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="btn-primary text-sm"
                  >
                    Get Started
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/dashboards')}
                  className="btn-primary text-sm"
                >
                  Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-5xl md:text-6xl font-display font-bold text-surface-900 dark:text-white mb-6">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-surface-600 dark:text-surface-400 max-w-3xl mx-auto mb-8">
          Choose the perfect plan for your business. Scale as you grow with our flexible pricing options.
        </p>
        
        {/* Currency Selector */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 shadow-sm">
            <CreditCardIcon className="w-5 h-5 text-surface-500" />
            <span className="text-sm text-surface-600 dark:text-surface-400">Currency:</span>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="bg-transparent border-none text-sm font-medium text-surface-900 dark:text-white focus:ring-0 cursor-pointer"
            >
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Billing Cycle Toggle (for future yearly pricing) */}
          {/* <div className="flex items-center gap-2 px-1 py-1 bg-surface-100 dark:bg-surface-800 rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Yearly <span className="text-green-500 ml-1">(Save 20%)</span>
            </button>
          </div> */}
        </div>
      </div>
      
      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {plansLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white dark:bg-surface-800 rounded-2xl p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-surface-200 dark:bg-surface-700 rounded w-3/4 mb-4" />
                  <div className="h-10 bg-surface-200 dark:bg-surface-700 rounded w-1/2 mb-6" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(j => (
                      <div key={j} className="h-4 bg-surface-200 dark:bg-surface-700 rounded" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {plans.map((plan) => {
              const features = PLAN_FEATURES[plan.slug];
              const isCurrent = isAuthenticated && plan.slug === currentPlan;
              const isRecommended = features?.recommended;
              
              return (
                <div
                  key={plan.slug}
                  className={`relative bg-white dark:bg-surface-900 rounded-2xl border-2 transition-all hover:shadow-2xl ${
                    isRecommended
                      ? 'border-primary-500 shadow-xl scale-105 lg:scale-110'
                      : isCurrent
                        ? 'border-green-500 shadow-lg'
                        : 'border-surface-200 dark:border-surface-800 hover:border-primary-300'
                  }`}
                >
                  {/* Recommended Badge */}
                  {isRecommended && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <div className="px-4 py-1 bg-gradient-to-r from-primary-500 to-accent-500 text-white text-sm font-bold rounded-full shadow-lg">
                        ⭐ Most Popular
                      </div>
                    </div>
                  )}
                  
                  {/* Current Plan Badge */}
                  {isCurrent && !isRecommended && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <div className="px-4 py-1 bg-green-500 text-white text-sm font-bold rounded-full shadow-lg">
                        Current Plan
                      </div>
                    </div>
                  )}
                  
                  <div className="p-6">
                    {/* Plan Icon & Name */}
                    <div className="text-center mb-6">
                      <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${features?.gradient || 'from-surface-400 to-surface-600'} flex items-center justify-center mb-4 shadow-lg`}>
                        <SparklesIcon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-display font-bold text-surface-900 dark:text-white mb-2">
                        {plan.name}
                      </h3>
                      {plan.description && (
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          {plan.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Price */}
                    <div className="text-center mb-6">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className={`text-4xl font-bold bg-gradient-to-r ${features?.gradient || 'from-surface-400 to-surface-600'} bg-clip-text text-transparent`}>
                          {formatPrice(plan)}
                        </span>
                        {!plan.isContactSales && plan.slug !== 'starter' && (
                          <span className="text-surface-500 dark:text-surface-400 text-sm">/month</span>
                        )}
                      </div>
                      {plan.slug === 'starter' && (
                        <p className="text-sm text-surface-500 mt-1">Forever free</p>
                      )}
                      {plan.slug === 'enterprise' && (
                        <p className="text-sm text-surface-500 mt-1">Starting at $1,000 - $20,000/mo</p>
                      )}
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {features?.features.slice(0, 8).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          {feature.included ? (
                            <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XMarkIcon className="w-5 h-5 text-surface-300 dark:text-surface-700 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm flex-1 ${
                            feature.included
                              ? 'text-surface-700 dark:text-surface-300'
                              : 'text-surface-400 dark:text-surface-600 line-through'
                          }`}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA Button */}
                    {isCurrent ? (
                      <button
                        disabled
                        className="w-full py-3 px-4 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold text-sm cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : plan.isContactSales ? (
                      <button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full py-3 px-4 rounded-xl bg-gradient-to-r ${features?.gradient || 'from-surface-400 to-surface-600'} text-white font-semibold text-sm hover:shadow-lg transition-all flex items-center justify-center gap-2`}
                      >
                        <EnvelopeIcon className="w-5 h-5" />
                        Contact Sales
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full py-3 px-4 rounded-xl ${
                          isRecommended
                            ? `bg-gradient-to-r ${features?.gradient} text-white shadow-lg hover:shadow-xl`
                            : 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white hover:bg-surface-200 dark:hover:bg-surface-700'
                        } font-semibold text-sm transition-all flex items-center justify-center gap-2`}
                      >
                        {isAuthenticated ? 'Upgrade Now' : 'Get Started'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Feature Comparison Table (Optional - for more detailed comparison) */}
        <div className="mt-20">
          <h2 className="text-3xl font-display font-bold text-center text-surface-900 dark:text-white mb-12">
            Compare All Features
          </h2>
          <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-50 dark:bg-surface-800/50 border-b border-surface-200 dark:border-surface-700">
                    <th className="text-left p-4 font-semibold text-surface-900 dark:text-white">
                      Feature
                    </th>
                    {plans.map(plan => (
                      <th key={plan.slug} className="text-center p-4 font-semibold text-surface-900 dark:text-white">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                  {/* User Seats */}
                  <tr>
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <UserGroupIcon className="w-5 h-5 text-surface-500" />
                        User Seats
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {plan.features?.userSeats === -1 ? 'Unlimited' : plan.features?.userSeats}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Storage */}
                  <tr className="bg-surface-50/50 dark:bg-surface-800/20">
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <CircleStackIcon className="w-5 h-5 text-surface-500" />
                        Storage
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {plan.features?.storageGB === -1 ? 'Unlimited' : `${plan.features?.storageGB} GB`}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Dashboards */}
                  <tr>
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <ChartBarIcon className="w-5 h-5 text-surface-500" />
                        Dashboards
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {plan.features?.dashboardLimit === -1 ? 'Unlimited' : plan.features?.dashboardLimit}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Sync Frequency */}
                  <tr className="bg-surface-50/50 dark:bg-surface-800/20">
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-surface-500" />
                        Sync Frequency
                      </div>
                    </td>
                    {plans.map(plan => {
                      const syncOption = plan.features?.syncOptions?.[plan.features?.syncOptions.length - 1];
                      const displayText = syncOption 
                        ? syncOption.replace('ily', 'y').replace('ly', '')
                        : 'Manual';
                      return (
                        <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                          {displayText}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Monthly Credits */}
                  <tr>
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-surface-500" />
                        AI Credits
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {plan.features?.monthlyCredits > 0 
                          ? `${plan.features.monthlyCredits.toLocaleString()}/mo`
                          : plan.features?.oneTimeCredits > 0
                            ? `${plan.features.oneTimeCredits} one-time`
                            : plan.slug === 'enterprise' ? 'Custom' : 'None'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Shared Dashboards */}
                  <tr className="bg-surface-50/50 dark:bg-surface-800/20">
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <ShareIcon className="w-5 h-5 text-surface-500" />
                        Shared Dashboards
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {['starter', 'basic'].includes(plan.slug) ? '1' : plan.slug === 'pro' ? 'Up to 5' : plan.slug === 'business' ? 'Up to 50' : 'Custom'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Share Credentials per Dashboard */}
                  <tr>
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <ShareIcon className="w-5 h-5 text-surface-500" />
                        Share Credentials per Dashboard
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {['starter', 'basic', 'pro'].includes(plan.slug) ? '1' : 'Unlimited'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Public Cache Refresh Rate */}
                  <tr className="bg-surface-50/50 dark:bg-surface-800/20">
                    <td className="p-4 text-surface-700 dark:text-surface-300 font-medium">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-surface-500" />
                        Public Cache Refresh Rate
                      </div>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.slug} className="p-4 text-center text-surface-600 dark:text-surface-400">
                        {plan.slug === 'starter' ? 'Min. 1 day' : plan.slug === 'basic' ? 'Min. 3 hours' : plan.slug === 'pro' ? 'Min. 1 hour' : 'Min. 30 minutes'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Credit Packs and Additional Seats Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-display font-bold text-center text-surface-900 dark:text-white mb-6">
            Add-Ons & Extensions
          </h2>
          <p className="text-center text-surface-600 dark:text-surface-400 mb-12 max-w-3xl mx-auto">
            Expand your plan with additional credits or team seats as your needs grow.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Credit Packs */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl border-2 border-surface-200 dark:border-surface-800 p-8 hover:border-primary-400 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                  Credit Packs
                </h3>
              </div>
              
              <p className="text-surface-600 dark:text-surface-400 mb-6">
                Need more AI credits? Purchase additional credit packs to power your analytics and insights.
              </p>
              
              <div className="space-y-4 mb-6">
                {creditPacks.length > 0 ? (
                  creditPacks.map((pack, index) => {
                    const price = pack.pricing?.find(p => p.currencyCode === selectedCurrency);
                    const isRecommended = index === 1; // Middle pack is typically best value
                    
                    return (
                      <div 
                        key={pack._id || pack.slug}
                        className={`flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg ${
                          isRecommended ? 'border-2 border-primary-400' : ''
                        }`}
                      >
                        <div>
                          <p className="font-semibold text-surface-900 dark:text-white">
                            {pack.credits.toLocaleString()} Credits
                          </p>
                          <p className="text-sm text-surface-500">One-time purchase</p>
                          {pack.bonusCredits > 0 && (
                            <p className="text-xs text-green-500 mt-1">
                              +{pack.bonusCredits} bonus credits
                            </p>
                          )}
                          {pack.savingsPercentage > 0 && (
                            <p className="text-xs text-green-500 mt-1">
                              Save {pack.savingsPercentage}%
                            </p>
                          )}
                        </div>
                        <p className="text-xl font-bold text-primary-500">
                          {price ? formatCurrencyAmount(price.amount, selectedCurrency) : 'N/A'}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-surface-500">
                    Loading credit packs...
                  </div>
                )}
              </div>
              
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    navigate('/admin/billing');
                  } else {
                    navigate('/register');
                  }
                }}
                className="w-full py-3 px-4 rounded-xl bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white hover:bg-surface-200 dark:hover:bg-surface-700 font-semibold text-sm transition-all"
              >
                {isAuthenticated ? 'Purchase Credits' : 'Get Started'}
              </button>
            </div>
            
            {/* Additional Seats */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl border-2 border-surface-200 dark:border-surface-800 p-8 hover:border-primary-400 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                  <UserGroupIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                  Additional Seats
                </h3>
              </div>
              
              <p className="text-surface-600 dark:text-surface-400 mb-6">
                Grow your team by adding more user seats to your subscription plan.
              </p>
              
              <div className="space-y-4 mb-6">
                {seatsPricing.length > 0 ? (
                  <>
                    {(() => {
                      // Find pricing for selected currency, or use first available
                      const pricing = seatsPricing.find(p => p.currencyCode === selectedCurrency) || seatsPricing[0];
                      
                      return (
                        <div className="p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg border-2 border-primary-400">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-surface-900 dark:text-white">
                              Additional User Seat
                            </p>
                            <p className="text-xl font-bold text-primary-500">
                              {formatCurrencyAmount(pricing.pricePerSeat, pricing.currencyCode)}/seat
                            </p>
                          </div>
                          <p className="text-sm text-surface-500">
                            Per month, per additional seat (all plans)
                          </p>
                        </div>
                      );
                    })()}
                    
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        <strong>Note:</strong> Available for Basic, Pro, Business, and Enterprise plans. 
                        Starter plan does not support additional seats - please upgrade to add team members.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-surface-500">
                    Loading seats pricing...
                  </div>
                )}
              </div>
              
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    navigate('/admin/billing');
                  } else {
                    navigate('/register');
                  }
                }}
                className="w-full py-3 px-4 rounded-xl bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white hover:bg-surface-200 dark:hover:bg-surface-700 font-semibold text-sm transition-all"
              >
                {isAuthenticated ? 'Add Seats' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>
        
        {/* FAQ or Additional Info Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-display font-bold text-surface-900 dark:text-white mb-6">
            Need Help Choosing?
          </h2>
          <p className="text-lg text-surface-600 dark:text-surface-400 mb-8 max-w-2xl mx-auto">
            Not sure which plan is right for you? Our team is here to help you find the perfect fit for your business needs.
          </p>
          <div className="flex items-center justify-center gap-4">
            {!isAuthenticated && (
              <button
                onClick={() => navigate('/register')}
                className="btn-primary text-lg px-8 py-3"
              >
                Start Free Trial
              </button>
            )}
            <button
              onClick={() => setShowContactSalesModal(true)}
              className="btn-secondary text-lg px-8 py-3"
            >
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              Contact Sales
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-surface-100 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-surface-600 dark:text-surface-400">
              © 2026 Neoshift BI. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/terms')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Sales Modal */}
      <ContactSalesModal 
        isOpen={showContactSalesModal} 
        onClose={() => setShowContactSalesModal(false)} 
      />
    </div>
  );
}
